import { Injectable } from '@angular/core';
import { LMContextService } from './lm-context.service';
import {
  LMFormStateKeyParams,
  LMFormStateService,
  LMListStateKeyParams,
  LMListStateService,
  LMPageStateKeyParams,
  LMPageStateService } from '@states/services';

import { FormResource } from '@resources/form.resource';
import { LMUrlInfoBuilderService } from './lm-url-info-builder.service';

@Injectable()
export class LMStateHelperService {

  constructor(
    private lmContextService: LMContextService,
    private lmFormStateService: LMFormStateService,
    private lmPageStateService: LMPageStateService,
    private lmListStateService: LMListStateService,
    private lmUrlInfoBuilderService: LMUrlInfoBuilderService,
  ) {}

  public buildFormStateKeyParams(routeResourceId: string, formSpec: LMFormSpec): LMFormStateKeyParams {
    const params = new LMFormStateKeyParams();

    params.actorId = this.lmContextService.getActorId()
    params.asOfDate = this.lmContextService.getAsOfDate();
    params.dataArea = this.lmContextService.getUrlPdlOrDa();
    params.routeResourceId = routeResourceId;

    const formResource: FormResource = new FormResource({
      busClassName: formSpec.busClassSpec ? formSpec.busClassSpec.name : 'pageContextForm',
      formName: formSpec.busFormSpec.name
    });
    params.resourceId = formResource.resourceId;

    return params;
  }

  public buildPageStateKeyParams(routeResourceId: string, pageSpec: LMPageSpec, pagePanelId: string): LMPageStateKeyParams {
    const params = new LMPageStateKeyParams();

    params.actorId = this.lmContextService.getActorId()
    params.asOfDate = this.lmContextService.getAsOfDate();
    params.dataArea = this.lmContextService.getUrlPdlOrDa();
    params.routeResourceId = routeResourceId;

    params.pagePanelId = pagePanelId;

    return params;
  }

  public buildListStateKeyParams(routeResourceId: string, listUrlInfo: LMListUrlInfo, listSpec: LMListSpec): LMListStateKeyParams {
    const params = new LMListStateKeyParams();

    params.actorId = this.lmContextService.getActorId();
    params.asOfDate = this.lmContextService.getAsOfDate();
    params.dataArea = this.lmContextService.getUrlPdlOrDa();
    params.routeResourceId = routeResourceId;

    params.prefix = listSpec.busListSpec.id;
    params.resourceId = listUrlInfo.resource ? listUrlInfo.resource.resourceId : undefined;
    params.userFolder = listSpec.userFolder;

    if (listUrlInfo.sourceResource) {
      // ---------------------------------------------------------------------------------------
      // for navigating to a list using a rel. Need to use the sourceResourceId to keep
      // the stateKey unique.
      // ---------------------------------------------------------------------------------------
      // example:
      // - Recruiter > Today > Anne Polk > Click View All on New Candidates dashboard panel >
      // click back > select Sarah Taylor > Click View All on New Candidates dashboard panel >
      // notice you are seeing Anne Polks new cnadidates and not Sarah Taylors. This is because
      // the state.currentUrl for Ann Polks was being used due to the state key not being
      // unique enough.
      // ---------------------------------------------------------------------------------------
      params.contextResourceId = listUrlInfo.sourceResource.resourceId;
    }

    return params;
  }

  public clearFormComponentState(routeResourceId: string, formSpec: LMFormSpec, includeSubComponents: boolean) {
    this.lmFormStateService.deleteFormState(this.buildFormStateKeyParams(routeResourceId, formSpec));

    if (includeSubComponents) {
      this.clearFormSubComponents(routeResourceId, formSpec);
    }
  }

  public clearPageComponentState(routeResourceId: string, pageSpec: LMPageSpec, prefix: string, includeSubComponents: boolean): void {
    this.lmPageStateService.deletePageState(this.buildPageStateKeyParams(routeResourceId, pageSpec, prefix));

    if (includeSubComponents) {
      this.clearPageSubComponents(routeResourceId, pageSpec);
    }
  }

  public clearListComponentState(routeResourceId: string, listUrlInfo: LMListUrlInfo, listSpec: LMListSpec, includeSubComponents: boolean): void {
    this.lmListStateService.deleteListState(this.buildListStateKeyParams(routeResourceId, listUrlInfo, listSpec));

    if (includeSubComponents) {
      this.clearListSubComponentsState(routeResourceId, listSpec);
    }
  }

  private clearFormSubComponents(routeResourceId: string, formSpec: LMFormSpec): void {
    if (!formSpec.busFormSpec.panels) {
      return;
    }

    formSpec.busFormSpec.panels.forEach(formPanelSpec => this.clearFormSubComponent(routeResourceId, formPanelSpec));
  }

  private clearFormSubComponent(routeResourceId: string, formPanelSpec: LMCompositePanelSpec) {
    switch (formPanelSpec.panelType) {
      case 'formlist':
        if (formPanelSpec) {
          const urlInfo = this.lmUrlInfoBuilderService.getListUrlInfoFromListSpec(formPanelSpec.listSpec, { comparePathAndSourceResourceOnly: true });
          this.clearListComponentState(routeResourceId, urlInfo, formPanelSpec.listSpec, true);
        }
        return;

      case 'dashboard':
        formPanelSpec.panels.forEach(dashboardPanelSpec => this.clearFormSubComponent(routeResourceId, dashboardPanelSpec));
        return;

      case 'multilist':
        this.clearMultiListComponentState(routeResourceId, formPanelSpec.lists);
        return;
    }
  }

  private clearPageSubComponents(routeResourceId: string, pageSpec: LMPageSpec): void {
    if (pageSpec.pageSpec.panels) {
      for (let i = 0; i < pageSpec.pageSpec.panels.length; i++) {
        const pagePanelSpec = pageSpec.pageSpec.panels[i];
        this.clearPagePanelComponentState(routeResourceId, pagePanelSpec);
      }
    }
  }

  private clearListSubComponentsState(routeResourceId: string, listSpec: LMListSpec): void {
    if (listSpec.busListSpec.detailSections) {
      this.clearBusDetailSectionState(routeResourceId, listSpec.busListSpec.detailSections);
    }
  }

  private clearPagePanelComponentState(routeResourceId: string, pagePanelSpec: LMPagePanelSpec): void {
    if ('Panel' === pagePanelSpec.panelType && pagePanelSpec.pane) {
      if ('ListPanel' === pagePanelSpec.pane.paneType && pagePanelSpec.pane.listSpec) {
        const urlInfo = this.lmUrlInfoBuilderService.getListUrlInfoFromListSpec(pagePanelSpec.pane.listSpec, { comparePathAndSourceResourceOnly: true });
        this.clearListComponentState(routeResourceId, urlInfo, pagePanelSpec.pane.listSpec, true);
      }
      return;
    }

    if ('MultiPanePanel' === pagePanelSpec.panelType && pagePanelSpec.panes) {
      this.clearMultiPaneComponentState(routeResourceId, pagePanelSpec.panes);
      return;
    }

    if ('DashBoardPanel' === pagePanelSpec.panelType && pagePanelSpec.panes) {
      this.clearPageDashboardPanelComponentState(routeResourceId, pagePanelSpec.panes);
      return;
    }
  }

  private clearMultiPaneComponentState(routeResourceId: string, multiPagePaneSpecList: Array<LMMultiPagePaneSpec>): void {
    for (let i = 0; i < multiPagePaneSpecList.length; i++) {
      const multiPagePaneSpec = multiPagePaneSpecList[i];
      if (multiPagePaneSpec.pane && 'ListPanel' === multiPagePaneSpec.pane.paneType && multiPagePaneSpec.pane.listSpec) {
        const urlInfo = this.lmUrlInfoBuilderService.getListUrlInfoFromListSpec(multiPagePaneSpec.pane.listSpec, { comparePathAndSourceResourceOnly: true });
        this.clearListComponentState(routeResourceId, urlInfo, multiPagePaneSpec.pane.listSpec, true);
      }
    }
  }
  private clearPageDashboardPanelComponentState(routeResourceId: string, dashboardPanelSpecs: Array<LMMultiPagePaneSpec>): void {
    dashboardPanelSpecs.forEach(dashboardPanelSpec => {
      if (dashboardPanelSpec.listSpec) {
        const urlInfo = this.lmUrlInfoBuilderService.getListUrlInfoFromListSpec(dashboardPanelSpec.listSpec, { comparePathAndSourceResourceOnly: true });
        this.clearListComponentState(routeResourceId, urlInfo, dashboardPanelSpec.listSpec, false);
      }
    })
  }

  private clearBusDetailSectionState(routeResourceId: string, detailSectionList: Array<LMBusDetailSection>): void {
    detailSectionList.forEach(detailSection => {
      switch (detailSection.type) {
        case 'list':
          const urlInfo = this.lmUrlInfoBuilderService.getListUrlInfoFromListSpec(detailSection.listSpec, { comparePathAndSourceResourceOnly: true });
          this.clearListComponentState(routeResourceId, urlInfo, detailSection.listSpec, true);
          return;

        case 'dashboard':
          this.clearBusDetailSectionState(routeResourceId, detailSection.detailSections);
          return;

        case 'multilist':
          this.clearMultiListComponentState(routeResourceId, detailSection.sections);
          return;
      }
    });
  }

  private clearMultiListComponentState(routeResourceId: string, multiListSpecs: Array<LMMultiListSectionSpec>) {
    multiListSpecs.forEach(multiListSpec => {
      if (multiListSpec.type === 'list') {
        const multiListUrlInfo = this.lmUrlInfoBuilderService.getListUrlInfoFromListSpec(multiListSpec.listSpec, { comparePathAndSourceResourceOnly: true });
        this.clearListComponentState(routeResourceId, multiListUrlInfo, multiListSpec.listSpec, false)
      }
    });
  }
}
